import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import { type Audit, type RatingLevel } from "@/services/api/guardiansv2/types";
import { isIssueFirstAudit, isAuditListable } from "../audits/auditPresentation";
import type { AuditOriginFilter } from "../guardiansParams";
import { useMissionControlSubscription } from "@/context/MissionControlWebSocketContext";
import type { GuardiansFilterValues } from "../shared/GuardiansFilterBar";
import { guardiansErrorMessage } from "../caughtError";
import { useGuardiansReconcile } from "../hooks/useGuardiansReconcile";
import {
  appendQueuedAudit,
  applyAuditCompleted,
  applyAuditRunning,
  auditSkeletonFromWs,
} from "../shared/auditWsPatch";

export type AuditBucket = "all" | "red" | "amber" | "green" | "failed";

const AUDIT_BUCKETS: readonly AuditBucket[] = ["all", "red", "amber", "green", "failed"];

export function isAuditBucket(value: string): value is AuditBucket {
  return (AUDIT_BUCKETS as readonly string[]).includes(value);
}

const PAGE_SIZE = 50;

export interface UseAuditsOptions {
  filters: GuardiansFilterValues;
  bucket: AuditBucket;
  search: string;
  auditOrigin?: AuditOriginFilter;
  selectedAuditId: string | null;
  enabled?: boolean;
}

export interface UseAuditsResult {
  audits: Audit[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  total: number;
  refresh: () => Promise<void>;
  refreshSelected: () => Promise<void>;
  loadMore: () => void;
  hasMore: boolean;
  selectedAudit: Audit | null;
  selectedLoading: boolean;
  /** Id whose fetch settled to "not found" (deleted / inaccessible); else null. */
  missingAuditId: string | null;
}

function includesNeedle(haystack: string | null | undefined, needle: string): boolean {
  return (haystack ?? "").toLowerCase().includes(needle);
}

function matchesSearch(audit: Audit, q: string): boolean {
  if (!q.trim()) return true;
  const needle = q.toLowerCase();
  const summary = audit.inputs?.summary?.toLowerCase() ?? "";
  if (summary.includes(needle) || audit._id.toLowerCase().includes(needle)) return true;
  const snapshot = audit.screening_snapshot;
  if (snapshot?.catalogue_key?.toLowerCase().includes(needle)) return true;
  if (snapshot?.alert_title?.toLowerCase().includes(needle)) return true;
  if (snapshot?.failed_checks?.some((check) => includesNeedle(check.title, needle))) {
    return true;
  }
  if (audit.report?.triggered_by?.some((label) => includesNeedle(label, needle))) {
    return true;
  }
  return false;
}

function matchesAuditOrigin(audit: Audit, origin: AuditOriginFilter): boolean {
  if (origin === "all") return true;
  return isIssueFirstAudit(audit);
}

function matchesBucket(audit: Audit, bucket: AuditBucket): boolean {
  if (bucket === "all") return true;
  if (bucket === "failed") return audit.status === "failed";
  return audit.rating === bucket;
}

/**
 * Loads and patches the audits list with optional WS updates.
 */
export function useAudits({
  filters,
  bucket,
  search,
  auditOrigin = "all",
  selectedAuditId,
  enabled = true,
}: UseAuditsOptions): UseAuditsResult {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [missingAuditId, setMissingAuditId] = useState<string | null>(null);

  const filtersKey = JSON.stringify({ filters, bucket });

  const fetchList = useCallback(
    async (append: boolean, currentSkip: number) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const ratingParam: RatingLevel | undefined =
          bucket === "red" || bucket === "amber" || bucket === "green"
            ? bucket
            : undefined;

        const res = await GuardiansV2API.listAudits({
          audit_type: filters.auditType === "all" ? undefined : filters.auditType,
          rating: ratingParam,
          template_id: filters.missionTemplateId || undefined,
          task_id: filters.taskId || undefined,
          agent_id: filters.agentId || undefined,
          catalogue_id: filters.catalogueId || undefined,
          time_range: "all",
          skip: currentSkip,
          limit: PAGE_SIZE,
        });

        let rows = res.audits;
        if (bucket === "failed") {
          rows = rows.filter((a) => a.status === "failed");
        }

        setAudits((prev) => (append ? [...prev, ...rows] : rows));
        setTotal(res.total);
        setSkip(currentSkip + rows.length);
      } catch (error) {
        setError(guardiansErrorMessage(error, "Failed to load audits"));
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [filters, bucket]
  );

  const refresh = useCallback(() => {
    setSkip(0);
    return fetchList(false, 0);
  }, [fetchList]);

  useEffect(() => {
    if (!enabled) {
      setAudits([]);
      setTotal(0);
      setSkip(0);
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      return;
    }
    setSkip(0);
    void fetchList(false, 0);
  }, [enabled, filtersKey, fetchList]);

  useGuardiansReconcile(refresh, enabled);

  useLayoutEffect(() => {
    if (!selectedAuditId) {
      setSelectedAudit(null);
      setSelectedLoading(false);
      setMissingAuditId(null);
      return;
    }
    let mounted = true;
    setSelectedLoading(true);
    setMissingAuditId(null);
    GuardiansV2API.getAudit(selectedAuditId)
      .then((doc) => {
        if (!mounted) return;
        setSelectedAudit(doc);
        if (doc == null) setMissingAuditId(selectedAuditId);
      })
      .catch(() => {
        if (!mounted) return;
        setSelectedAudit(null);
        setMissingAuditId(selectedAuditId);
      })
      .finally(() => {
        if (mounted) setSelectedLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedAuditId]);

  const selectedRef = useRef(selectedAuditId);
  selectedRef.current = selectedAuditId;

  const refreshSelected = useCallback(async () => {
    if (!selectedAuditId) return;
    try {
      const doc = await GuardiansV2API.getAudit(selectedAuditId);
      setSelectedAudit(doc);
    } catch {
      setSelectedAudit(null);
    }
  }, [selectedAuditId]);

  useMissionControlSubscription({
    onAuditQueued: (p) => {
      setAudits((prev) => appendQueuedAudit(prev, p));
      if (selectedRef.current === p.audit_id) {
        setSelectedAudit((current) =>
          current && current._id === p.audit_id
            ? { ...auditSkeletonFromWs(p), status: "queued" }
            : auditSkeletonFromWs(p)
        );
      }
    },
    onAuditRunning: (p) => {
      setAudits((prev) => applyAuditRunning(prev, p));
      if (selectedRef.current === p.audit_id) {
        setSelectedAudit((current) =>
          current && current._id === p.audit_id
            ? { ...current, status: "running" }
            : { ...auditSkeletonFromWs(p), status: "running" }
        );
      }
    },
    onAuditCompleted: (p) => {
      setAudits((prev) => applyAuditCompleted(prev, p));
      if (selectedRef.current === p.audit_id) {
        void GuardiansV2API.getAudit(p.audit_id).then(setSelectedAudit).catch(() => {});
      }
    },
  });

  const visibleAudits = useMemo(
    () =>
      audits.filter(
        (a) =>
          isAuditListable(a) &&
          matchesBucket(a, bucket) &&
          matchesSearch(a, search) &&
          matchesAuditOrigin(a, auditOrigin)
      ),
    [audits, bucket, search, auditOrigin]
  );

  const hasMore = skip < total;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    void fetchList(true, skip);
  }, [hasMore, loading, loadingMore, fetchList, skip]);

  return {
    audits: visibleAudits,
    loading,
    loadingMore,
    error,
    total,
    refresh,
    refreshSelected,
    loadMore,
    hasMore,
    selectedAudit,
    selectedLoading,
    missingAuditId,
  };
}
